/**
 * library — high-level facade over the schema module. Owns read/write
 * for the visitor's single "library" (a Queue + a tiny settings blob).
 *
 * Per contracts/storage-schema.md, load() is permissive: it drops
 * invalid items rather than rejecting the whole blob, so a future
 * schema tweak doesn't vaporize someone's channel.
 */

import type { LibraryBlobV1, Queue } from "../../types.ts";
import { clearBlob, loadBlob, saveBlob } from "./schema.ts";

export interface LibrarySettings {
  autoplayUnlocked: boolean;
  /**
   * True once the visitor has added at least one item of their own.
   * Used to decide whether to overwrite the demo channel on first
   * paste (FR-027 / spec US4 scenario 2).
   */
  userOwnsQueue: boolean;
}

export const defaultSettings: LibrarySettings = {
  autoplayUnlocked: false,
  userOwnsQueue: false,
};

export interface Library {
  queue: Queue;
  settings: LibrarySettings;
}

export function loadLibrary(): Library | null {
  const blob = loadBlob();
  if (!blob) return null;
  return { queue: blob.queue, settings: blob.settings };
}

export function saveLibrary(lib: Library): void {
  const blob: LibraryBlobV1 = {
    version: 1,
    queue: lib.queue,
    settings: lib.settings,
    updatedAt: Date.now(),
  };
  saveBlob(blob);
}

export function clearLibrary(): void {
  clearBlob();
}
